import { t } from '@lingui/core/macro';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { isEmpty, isNil } from 'lodash';
import { isEmailValid } from 'utils/validations';
import * as Yup from 'yup';

declare module 'yup' {
  interface DateSchema {
    requiredIfOtherPresent(this: Yup.DateSchema, relatedField: string, errorMessage: string): this;
  }

  interface StringSchema {
    minLength(
      this: Yup.StringSchema,
      fieldKey: string,
      minLength?: number,
      nullable?: boolean
    ): this;
    maxLength(this: Yup.StringSchema, fieldKey: string, maxLength?: number): this;
    eq(this: Yup.StringSchema, fieldKey: string, to: string, error: string): this;
    validEmail(this: Yup.StringSchema, fieldKey: string): this;
    validPhoneNumber(this: Yup.StringSchema, fieldKey: string): this;
    isRequired(this: Yup.StringSchema, fieldKey: string, required: boolean): this;
  }
}

// DATE SCHEMA METHODS

Yup.addMethod(
  Yup.date,
  'requiredIfOtherPresent',
  function (this: Yup.DateSchema, relatedField: string, errorMessage: string) {
    return this.when(relatedField, {
      is: (val: unknown) => !!val,
      then: (schema: Yup.DateSchema) => schema.required(errorMessage),
    });
  }
);

// STRING SCHEMA METHODS

Yup.addMethod(
  Yup.string,
  'minLength',
  function (
    this: Yup.StringSchema<string | null | undefined>,
    fieldKey: string,
    minLength?: number,
    required = true
  ) {
    let schema = this;
    if (!required) {
      schema = schema.notRequired();
    }
    return schema.test(fieldKey, minLength ? t`Minimum de ${minLength} caractères` : '', (val) => {
      if ((!required && isNil(val)) || isEmpty(val)) {
        return true;
      }
      if (minLength === undefined || (val && val.length >= minLength)) {
        return true;
      }

      return false;
    });
  }
);

Yup.addMethod(
  Yup.string,
  'maxLength',
  function (
    this: Yup.StringSchema<string | null | undefined>,
    fieldKey: string,
    maxLength: number
  ) {
    return this.test(fieldKey, t`Maximum de ${maxLength} caractères`, (val) => {
      if (maxLength === undefined || !val || val.length <= maxLength) {
        return true;
      }

      return false;
    });
  }
);

Yup.addMethod(
  Yup.string,
  'eq',
  function (this: Yup.StringSchema, fieldKey: string, to: string, error: string) {
    return this.test(fieldKey, error, (val) => val === to);
  }
);

Yup.addMethod(Yup.string, 'validEmail', function (this: Yup.StringSchema, fieldKey: string) {
  return this.test(
    fieldKey,
    t`Le format de l’adresse e-mail est incorrect (pas d’espaces, pas de lettres majuscules, pas de caractères spéciaux).`,
    (email) => !email || isEmailValid(email)
  );
});

Yup.addMethod(Yup.string, 'validPhoneNumber', function (this: Yup.StringSchema, fieldKey: string) {
  return this.test(
    fieldKey,
    t`Le format du numéro de téléphone est incorrect`,
    (phoneNumber) => !phoneNumber || isValidPhoneNumber(phoneNumber)
  );
});

Yup.addMethod(
  Yup.string,
  'isRequired',
  function (this: Yup.StringSchema, fieldKey: string, required: boolean) {
    return this.test(fieldKey, t`Ce champ est requis.`, (value) => !!value || !required);
  }
);
